import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightlightIcon from "@mui/icons-material/Nightlight";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Badge from "@mui/material/Badge";
import StarBorderPurple500Icon from "@mui/icons-material/StarBorderPurple500";
import Tooltip from "@mui/material/Tooltip";
import MenuIcon from "@mui/icons-material/Menu";

import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  Drawer,
} from "@mui/material";
import logo from "../assets/images/logo.png";
import AuthService from "../utils/AuthService";
import { authoringSectionsByKey } from "../data/authoringSections";

const AUTHORING_NAV_KEYS = [
  "items",
  "lessons",
  "enrollments",
  "contacts",
  "assets",
  "assignments",
  "taxonomy",
];

const Appbar = ({
  isMobile,
  themeMode,
  toggleTheme,
  onAboutClick,
  onInstClick,
  ontestimonialClick,
  onCompareProClick,
  onQARefClick,
  assignmentDueCount,
  handleDrawerToggle = () => {},
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [bookmarkedCount, setBookmarkedCount] = useState(0);
  const user = AuthService.getUserInfo();
  const userToken = user?.token;
  const navigate = useNavigate();
  const location = useLocation();
  const isViewCoursePage = location.pathname === "/coursepage";
  const isassignmentsduepage = location.pathname === "/assignmentsdue";
  const isbookmarkedlessonspage = location.pathname === "/bookmarkedlessons";
  const isMainpage = location.pathname.includes("/Modules/Course/");
  const isGradePage = location.pathname.includes("/Grade/course/");
  const isAuthoringPage = location.pathname.startsWith("/authoring");
  const authoringNavItems = useMemo(
    () =>
      AUTHORING_NAV_KEYS.map((key) => {
        const section = authoringSectionsByKey[key];
        if (!section || !section.types || section.types.length === 0) {
          return null;
        }
        const [firstType] = section.types;
        return {
          key,
          label: section.label,
          path: `/authoring/${section.key}/${firstType.key}`,
        };
      }).filter(Boolean),
    []
  );
  const handleProfileDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleLogout = () => {
    AuthService.logOut();
    setDrawerOpen(false);
    navigate("/");
  };
  const handleGoToCourses = () => {
    navigate("coursepage"); // Adjust the route if needed
  };

  const handleGoToAuthoring = () => {
    setDrawerOpen(false);
    navigate("/authoring");
  };

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("bookmarkedLessons")) || [];
    setBookmarkedCount(stored.length);

    if (!userToken) {
      return;
    }

    const expiry = AuthService.getTokenExpiryTime();
    if (!expiry || expiry < new Date()) {
      console.log("Token expired. Logging out...");
      AuthService.logOut();
      navigate("/");
    }
  }, [navigate, userToken]);

  const hasAuthoringAccess = useMemo(() => {
    if (!user) {
      return false;
    }

    const coursesMap = user.courses || user.mycourses;
    if (!coursesMap) {
      return false;
    }

    const courseList = Array.isArray(coursesMap)
      ? coursesMap
      : Object.values(coursesMap);

    return courseList.some((course) => Number(course?.roleId) === 2);
  }, [user]);

  return (
    <AppBar position="static" color="transparent" elevation={0}>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Open navigation menu"
              onClick={handleDrawerToggle}
              size="large"
            >
              <MenuIcon />
            </IconButton>
          )}
          <Link to="/" style={{ textDecoration: "none" }}>
            <img
              src={logo}
              alt="Logo"
              style={{
                height: isViewCoursePage || isMainpage ? 40 : 70,
                cursor: "pointer",
              }}
            />
          </Link>
        </Box>
        {/* Left side: Logo and nav buttons */}
        {!isAuthoringPage &&
          !isViewCoursePage &&
          !isMobile &&
          !isMainpage &&
          !isassignmentsduepage &&
          !isGradePage &&
          !isbookmarkedlessonspage && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <Button sx={{ fontSize: "1rem" }} onClick={onAboutClick}>
                  About the Course
                </Button>
                <Button component={Link} to="/enroll" sx={{ fontSize: "1rem" }}>
                  Enroll
                </Button>
                <Button sx={{ fontSize: "1rem" }} onClick={onInstClick}>
                  Instructor
                </Button>
                <Button sx={{ fontSize: "1rem" }} onClick={ontestimonialClick}>
                  Testimonials
                </Button>
                <Button sx={{ fontSize: "1rem" }} onClick={onCompareProClick}>
                  Compare Programs
                </Button>
                <Button sx={{ fontSize: "1rem" }} onClick={onQARefClick}>
                  Q&A
                </Button>
                <Button
                  sx={{ fontSize: "1rem" }}
                  component={Link}
                  to="/contact"
                >
                  Contact
                </Button>
              </Box>
            </Box>
          )}

        {!isMobile && isAuthoringPage && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
              }}
            >
              {authoringNavItems.map((item) => {
                const isActive = location.pathname.startsWith(
                  `/authoring/${item.key}`
                );
                return (
                  <Button
                    key={item.key}
                    onClick={() => navigate(item.path)}
                    sx={{
                      fontSize: "1rem",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? "primary.main" : "text.primary",
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Right side: Avatar/Sign In button (ALWAYS visible) */}
        <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
          {user ? (
            <>
              <Button
                onClick={handleGoToCourses}
                variant="text"
                disableRipple
                sx={{
                  textDecoration: "underline",
                  textDecorationColor: "#424242",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                }}
              >
                My Courses
              </Button>

              <Tooltip title="View favourite lessons" arrow>
                <IconButton
                  onClick={() =>
                    navigate("/bookmarkedlessons", {
                      state: {
                        title: "bookmarkedlessons",
                      },
                    })
                  }
                  sx={{ ml: 1 }}
                >
                  <Badge
                    badgeContent={bookmarkedCount}
                    color="error"
                    invisible={bookmarkedCount === 0}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.6rem", // smaller text
                        height: 16, // adjust size
                        minWidth: 16,
                        padding: "0 4px",
                      },
                    }}
                  >
                    <StarBorderPurple500Icon />
                  </Badge>
                </IconButton>
              </Tooltip>
              <Tooltip title="View pending assignments" arrow>
                <IconButton
                  onClick={() =>
                    navigate("/assignmentsdue", {
                      state: {
                        title: "assignmentsdue",
                      },
                    })
                  }
                >
                  <Badge
                    badgeContent={
                      assignmentDueCount === undefined ||
                      assignmentDueCount === null
                        ? null
                        : assignmentDueCount
                    }
                    color="error"
                    showZero
                  >
                    <NotificationsIcon />
                  </Badge>
                </IconButton>
              </Tooltip>

              <IconButton
                onClick={handleProfileDrawerToggle}
                size="small"
                sx={{ ml: 1 }}
              >
                <Avatar
                  alt={user.firstName}
                  src={user.avatarUrl || ""}
                  sx={{
                    bgcolor: "#424242",
                    color: "#fff",
                    width: 40,
                    height: 40,
                  }}
                >
                  {user.firstName?.charAt(0)}
                </Avatar>
              </IconButton>

              <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleProfileDrawerToggle}
              >
                <Box
                  sx={{
                    width: 280,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 3,
                  }}
                  role="presentation"
                  onClick={handleProfileDrawerToggle}
                  onKeyDown={handleProfileDrawerToggle}
                >
                  <Avatar
                    alt={user.firstName}
                    src={user.avatarUrl || ""}
                    sx={{
                      bgcolor: "#424242",
                      color: "#fff",
                      width: 64,
                      height: 64,
                      mb: 2,
                      fontSize: 28,
                    }}
                  >
                    {user.firstName?.charAt(0)}
                  </Avatar>
                  <Typography variant="h6" gutterBottom>
                    {user.firstName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {user.name}
                  </Typography>
                  <Divider sx={{ my: 2, width: "100%" }} />
                  {hasAuthoringAccess && (
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGoToAuthoring();
                      }}
                      sx={{ mb: 2 }}
                    >
                      Authoring
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="error"
                    fullWidth
                    onClick={handleLogout}
                    sx={{ mb: 2 }}
                  >
                    Sign Out
                  </Button>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">Theme:</Typography>
                    {themeMode === "light" ? (
                      <WbSunnyIcon
                        sx={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTheme();
                        }}
                      />
                    ) : (
                      <NightlightIcon
                        sx={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTheme();
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Drawer>
            </>
          ) : (
            <Link to="/loginPage" style={{ textDecoration: "none" }}>
              <Button variant="contained">Sign In</Button>
            </Link>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Appbar;
